import React from 'react';
import LegalPageLayout from '@/components/LegalPageLayout';

const SECTIONS = [
  {
    id: 'overview',
    title: 'Overview',
    content: (
      <>
        <p>
          This Privacy Policy explains how Zap AI (<strong>"Zap AI"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, or <strong>"our"</strong>) collects, uses, shares, and protects personal information when you visit our website at <a href="https://www.zapai.com.au" className="text-blue-600 hover:underline">www.zapai.com.au</a> or interact with us through other channels.
        </p>
        <p>
          Zap AI is the parent company behind a portfolio of AI-powered platforms for service businesses. Each product (currently Zalma, our pet grooming salon management platform) has its own privacy practices that may apply in addition to this Policy. Where a product Privacy Policy is more specific, the product policy applies to data collected through that product.
        </p>
        <p>
          We are committed to handling personal information in accordance with the Australian Privacy Principles set out in the <em>Privacy Act 1988</em> (Cth) and applicable data protection laws.
        </p>
      </>
    ),
  },
  {
    id: 'who-this-applies-to',
    title: 'Who this applies to',
    content: (
      <>
        <p>This Privacy Policy applies to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Visitors to www.zapai.com.au and any sub-page that links to this Policy.</li>
          <li>People who contact us through demo requests, support emails, or sales enquiries.</li>
          <li>Job applicants who apply for a role at Zap AI.</li>
          <li>Subscribers to any Zap AI newsletter or marketing communications.</li>
        </ul>
        <p>
          Customers of our products (for example, Zalma users) should also review the privacy policy of the relevant product, which contains additional details about how data is processed within that product.
        </p>
      </>
    ),
  },
  {
    id: 'information-we-collect',
    title: 'Information we collect',
    content: (
      <>
        <p><strong>Information you provide</strong></p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Demo and sales enquiries</strong>: name, email, phone number, business name, and any details you include in your message.</li>
          <li><strong>Newsletter signups</strong>: email address and any preferences you provide.</li>
          <li><strong>Support communications</strong>: messages you send us via email or web forms.</li>
          <li><strong>Job applications</strong>: CV, cover letter, contact details, and any information you choose to share.</li>
        </ul>
        <p className="pt-2"><strong>Information collected automatically</strong></p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Usage data: pages visited, time on page, referring URL, and links clicked.</li>
          <li>Device and log data: IP address, browser type, operating system, and timestamps.</li>
          <li>Cookies and similar technologies, as described below.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'how-we-use',
    title: 'How we use information',
    content: (
      <>
        <p>We use personal information to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Respond to your enquiries and schedule demos.</li>
          <li>Improve our website and marketing content.</li>
          <li>Send transactional communications about your enquiry.</li>
          <li>Send marketing communications where you have consented or where we are otherwise permitted under the Spam Act 2003. You can unsubscribe at any time.</li>
          <li>Evaluate job applications.</li>
          <li>Detect and prevent fraud and abuse.</li>
          <li>Comply with legal obligations.</li>
        </ul>
        <p>We do not sell personal information.</p>
      </>
    ),
  },
  {
    id: 'how-we-share',
    title: 'How we share information',
    content: (
      <>
        <p>We share personal information only as described below:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Service providers</strong>: trusted third parties that help us operate our website and business, including hosting (AWS), email delivery, analytics, and customer support tooling.</li>
          <li><strong>Legal and safety</strong>: when required by law or to protect the rights, property, or safety of Zap AI, our customers, or the public.</li>
          <li><strong>Business transfers</strong>: in connection with a merger, acquisition, or asset sale, subject to confidentiality protections.</li>
          <li><strong>With your consent</strong>: in any other case where you have given us specific consent.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies and tracking',
    content: (
      <>
        <p>
          We use cookies and similar technologies to keep the website working, understand how it is used, and remember your preferences.
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Strictly necessary cookies</strong> support core website functionality.</li>
          <li><strong>Analytics cookies</strong> help us understand traffic and usage. Analytics is anonymised where possible.</li>
          <li><strong>Preference cookies</strong> remember non-essential settings.</li>
        </ul>
        <p>You can manage cookies through your browser settings.</p>
      </>
    ),
  },
  {
    id: 'cross-border',
    title: 'Cross-border processing',
    content: (
      <>
        <p>
          Our website infrastructure is hosted in Australia. Some operational tools we use (for example, analytics and email delivery) may be operated by providers headquartered outside Australia. Where this is the case, we ensure those providers offer privacy protections substantially similar to the Australian Privacy Principles.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'Data retention',
    content: (
      <>
        <p>
          We retain personal information only as long as necessary to fulfil the purposes for which it was collected, comply with our legal obligations, and resolve disputes.
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Demo and sales enquiry records: typically 24 months from the last interaction.</li>
          <li>Job application data: 12 months unless we hire you, in which case longer retention applies under HR policies.</li>
          <li>Marketing preferences (including unsubscribes): retained indefinitely so we honour your choice.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'security',
    title: 'Security',
    content: (
      <>
        <p>
          We use appropriate technical and organisational measures to protect personal information, including encryption in transit, role-based access controls, and audit logging on sensitive actions. No system is completely secure. If we detect a data breach that affects you, we will notify you in accordance with our obligations under the Notifiable Data Breaches scheme.
        </p>
      </>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your rights and choices',
    content: (
      <>
        <p>Subject to applicable law, you have the right to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Access the personal information we hold about you.</li>
          <li>Correct inaccurate or incomplete information.</li>
          <li>Delete your personal information, subject to retention obligations described above.</li>
          <li>Withdraw consent for marketing communications using the unsubscribe link in our emails.</li>
          <li>Make a complaint if you believe we have mishandled your information.</li>
        </ul>
        <p>
          To exercise any of these rights, contact us using the details below. If you are not satisfied with our response, you can contact the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au.
        </p>
      </>
    ),
  },
  {
    id: 'children',
    title: 'Children',
    content: (
      <>
        <p>
          Our website is not directed to children under 16. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, contact us and we will delete it.
        </p>
      </>
    ),
  },
  {
    id: 'third-party',
    title: 'Third-party links',
    content: (
      <>
        <p>
          Our website may link to third-party services. Their privacy practices are governed by their own policies, not this one. Review the privacy notice of any third-party service before sharing personal information with it.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    content: (
      <>
        <p>
          We may update this Privacy Policy from time to time. We will post the updated version on this page and update the "Last updated" date at the top. For material changes, we will provide additional notice. Your continued use of the website after the updated policy takes effect indicates your acceptance.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    title: 'Contact us',
    content: (
      <>
        <p>
          If you have any questions about this Privacy Policy, want to exercise any of your rights, or wish to make a complaint, please contact us:
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Email: <a href="mailto:info@zapai.com.au" className="text-blue-600 hover:underline">info@zapai.com.au</a></li>
          <li>Mail: Zap AI, Privacy Team, Australia</li>
        </ul>
        <p>We aim to respond to privacy enquiries within 30 days.</p>
      </>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="May 2, 2026"
      sections={SECTIONS}
    />
  );
}
